import React, {RefObject} from "react";
import {NewPromptInput} from "./new_prompt_input";
import {styled} from "styled-components";
import {Button, Grid, withStyles} from "@material-ui/core";

interface ChatFooterProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  generateLlmPrompt: () => void;
  feedback: string | null;
  setFeedback: (feedback: string) => void;
  cancelFeedback: () => void;
  sendFeedback: () => void;
  showFeedbackInput: boolean;
  canMakeQueries: boolean;
  showFeedbackSuccessMsg: boolean;
  isLoading: boolean;
  textFieldRef: RefObject<HTMLInputElement>;
  compact?: boolean;
}

const ChatFooterContainer = styled.div<{
  showFeedbackInput?: boolean;
  showFeedbackSuccessMsg?: boolean;
}>`
  background: ${({showFeedbackInput, showFeedbackSuccessMsg}) =>
    showFeedbackInput || showFeedbackSuccessMsg ? "#e6f4ff;" : "none"};
  padding: 4px;
`; // padding compensates for MUI grid -4px margin causing overflows

const styles = {
  compactFeedbackMessage: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 0 6px",
    fontSize: ".75em",
    fontStyle: "italic",
  },
} as const;

const GridFeedbackMessage = withStyles({
  root: {
    display: "flex",
    alignItems: "center",
    fontSize: ".75em",
    fontStyle: "italic",
  },
})(Grid);

function ChatFooter({
  prompt,
  setPrompt,
  generateLlmPrompt,
  feedback,
  setFeedback,
  cancelFeedback,
  sendFeedback,
  showFeedbackInput,
  canMakeQueries,
  showFeedbackSuccessMsg,
  isLoading,
  textFieldRef,
  compact,
}: ChatFooterProps) {
  if (!canMakeQueries) {
    return <CannotReplyMessage />;
  }

  if (compact) {
    return (
      <ChatFooterContainer
        showFeedbackInput={showFeedbackInput}
        showFeedbackSuccessMsg={showFeedbackSuccessMsg}
        style={{padding: "6px 8px"}}
      >
        {showFeedbackSuccessMsg && (
          <div style={styles.compactFeedbackMessage}>Feedback Sent!</div>
        )}
        {!showFeedbackSuccessMsg && showFeedbackInput ? (
          <>
            <div style={styles.compactFeedbackMessage}>
              <span>Send feedback for highlighted response.</span>
              <Button
                size="small"
                style={{fontSize: "0.75em", marginLeft: "auto"}}
                variant="outlined"
                onClick={() => {
                  cancelFeedback();
                }}
              >
                Cancel
              </Button>
            </div>
            <NewPromptInput
              prompt={feedback ?? ""}
              setPrompt={setFeedback}
              generateLlmPrompt={sendFeedback}
              isLoading={false}
              textFieldRef={textFieldRef}
              compact
            />
          </>
        ) : (
          <NewPromptInput
            prompt={prompt}
            setPrompt={setPrompt}
            generateLlmPrompt={generateLlmPrompt}
            isLoading={isLoading}
            textFieldRef={textFieldRef}
            compact
          />
        )}
      </ChatFooterContainer>
    );
  }

  return (
    <ChatFooterContainer
      showFeedbackInput={showFeedbackInput}
      showFeedbackSuccessMsg={showFeedbackSuccessMsg}
    >
      <Grid container spacing={1} style={{padding: "0.5em"}}>
        {showFeedbackSuccessMsg && (
          <GridFeedbackMessage item xs={12}>
            <span>Feedback Sent!</span>
          </GridFeedbackMessage>
        )}
        {!showFeedbackSuccessMsg && showFeedbackInput ? (
          <>
            <GridFeedbackMessage item xs={10}>
              <span>Send feedback for highlighted response.</span>
            </GridFeedbackMessage>
            <Grid item xs={2}>
              <Button
                fullWidth
                style={{height: "100%", fontSize: "0.75em"}}
                variant="outlined"
                onClick={() => {
                  cancelFeedback();
                }}
              >
                Cancel
              </Button>
            </Grid>
            <NewPromptInput
              prompt={feedback ?? ""}
              setPrompt={setFeedback}
              generateLlmPrompt={sendFeedback}
              isLoading={false}
              textFieldRef={textFieldRef}
            />
          </>
        ) : (
          <NewPromptInput
            prompt={prompt}
            setPrompt={setPrompt}
            generateLlmPrompt={generateLlmPrompt}
            isLoading={isLoading}
            textFieldRef={textFieldRef}
          />
        )}
      </Grid>
    </ChatFooterContainer>
  );
}

function CannotReplyMessage() {
  return (
    <div style={{textAlign: "center", padding: "1em"}}>
      <i style={{color: "grey"}}>
        You do not have permission to reply to this conversation.
      </i>
    </div>
  );
}

export default ChatFooter;
